import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useToast } from '../../contexts/ToastContext';
import { Modal, ModalFooter } from '../ui/modal';
import { FormGroup, FormLabel, FormInput, FormTextarea, FormSelect } from '../ui/form';

function EditTaskModal({ show, handleClose, task, projects, onTaskUpdated }) {
  const [taskName, setTaskName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskProjectId, setTaskProjectId] = useState('');
  const [taskPriority, setTaskPriority] = useState(3);
  const [isLoading, setIsLoading] = useState(false);
  const addToast = useToast();

  useEffect(() => {
    if (task) {
      setTaskName(task.name || '');
      setTaskDescription(task.description || '');
      setTaskProjectId(task.projects?.id || '');
      setTaskPriority(task.priority || 3);
    }
  }, [task]);

  const handleSave = async () => {
    if (!taskName.trim()) {
      addToast('Task name is required', 'error');
      return;
    }

    if (!taskProjectId) {
      addToast('Please select a project', 'error');
      return;
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update({ 
          name: taskName, 
          description: taskDescription, 
          project_id: taskProjectId,
          priority: taskPriority
        })
        .eq('id', task.id)
        .select(`
          id, 
          name, 
          description, 
          priority, 
          created_at,
          projects!inner (id, name, project_groups!inner (id, name))
        `);

      if (error) {
        console.error('Error updating task:', error);
        addToast('Error updating task: ' + error.message, 'error');
      } else {
        addToast('Task updated successfully', 'success');
        onTaskUpdated(data[0]);
        handleClose();
      }
    } catch (error) {
      console.error('Error updating task:', error);
      addToast('Error updating task: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={show} onClose={handleClose} title="Edit Task">
      <div className="space-y-4">
          <FormGroup>
            <FormLabel htmlFor="taskName">Task Name *</FormLabel>
            <FormInput
              id="taskName"
              type="text"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder="Enter task name"
              disabled={isLoading}
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="taskDescription">Description</FormLabel>
            <FormTextarea
              id="taskDescription"
              rows={3}
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Enter task description"
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="taskProject">Project *</FormLabel>
            <FormSelect
              id="taskProject"
              value={taskProjectId}
              onChange={(e) => setTaskProjectId(e.target.value)}
              disabled={isLoading}
            >
              <option value="">Select a project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.project_groups?.name})
                </option>
              ))}
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="taskPriority">Priority</FormLabel>
            <FormSelect
              id="taskPriority"
              value={taskPriority}
              onChange={(e) => setTaskPriority(Number(e.target.value))}
              disabled={isLoading}
            >
              <option value={1}>Critical</option>
              <option value={2}>High</option>
              <option value={3}>Medium</option>
              <option value={4}>Low</option>
              <option value={5}>Lowest</option>
            </FormSelect>
          </FormGroup>
        </div>
        
        <ModalFooter>
        <button
          type="button"
          onClick={handleClose}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Updating...' : 'Update Task'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default EditTaskModal;
